import { useEffect, useState } from 'react'
import { getSettings, setSetting } from '../lib/apiPhpAdapter'
import {
  MODULE_DEV_KEYS,
  isModuleDevRestricted,
  parseModuleDevFlags,
  type ModuleDevFlags,
} from '../lib/moduleDevAccess'
import { DOVOLENA_MODULE_TITLE } from '../lib/dovolenaRequestType'
import './ModuleDevAccessPanel.css'

const LABELS: { key: keyof ModuleDevFlags; title: string; desc: string }[] = [
  {
    key: 'fksp',
    title: 'Žádosti FKSP',
    desc: 'Uzavřeno: přístup jen ředitel, hlavní ekonom, správce IT. Otevřeno: všichni zaměstnanci podle rolí ve FKSP.',
  },
  {
    key: 'dovolena',
    title: DOVOLENA_MODULE_TITLE,
    desc: 'Uzavřeno: jen ředitel, hlavní ekonom, správce IT. Otevřeno: žadatelé, nadřízení, personalista podle pravidel modulu.',
  },
  {
    key: 'travel_orders',
    title: 'Cestovní příkaz',
    desc: 'Uzavřeno: + zástupce ředitele. Otevřeno: všichni podle stavu příkazu a role.',
  },
]

export function ModuleDevAccessPanel() {
  const [flags, setFlags] = useState<ModuleDevFlags | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const s = await getSettings()
      setFlags(parseModuleDevFlags(s))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Chyba načtení')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const toggle = async (key: keyof ModuleDevFlags) => {
    if (!flags) return
    const settingKey = MODULE_DEV_KEYS[key]
    const currentlyRestricted = flags[key]
    const newValue = currentlyRestricted ? '0' : '1'
    setSaving(settingKey)
    setError(null)
    try {
      await setSetting(
        settingKey,
        newValue,
        key === 'fksp'
          ? 'FKSP vývoj'
          : key === 'dovolena'
            ? 'Dovolená vývoj'
            : 'Cestovní příkaz vývoj'
      )
      setFlags((f) => (f ? { ...f, [key]: isModuleDevRestricted(newValue) } : f))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Uložení selhalo')
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return <div className="module-dev-panel loading">Načítání…</div>
  }

  return (
    <div className="module-dev-panel settings-section">
      <h2>Přístup k modulům (režim vývoje)</h2>
      <p className="module-dev-intro">
        Správce IT může modul <strong>uzavřít</strong> — pak k němu mají přístup jen{' '}
        <strong>ředitel</strong>, <strong>hlavní ekonom</strong> a <strong>správce IT</strong>; u
        cestovních příkazů navíc <strong>zástupce ředitele</strong>. Po <strong>otevření</strong>{' '}
        platí běžná oprávnění pro zaměstnance.
      </p>
      {error && <div className="module-dev-error">{error}</div>}
      <ul className="module-dev-list">
        {LABELS.map(({ key, title, desc }) => {
          const restricted = flags?.[key] ?? true
          return (
            <li key={key} className="module-dev-row">
              <div className="module-dev-text">
                <strong>{title}</strong>
                <span className="module-dev-desc">{desc}</span>
              </div>
              <div className="module-dev-actions">
                <span className={`module-dev-badge ${restricted ? 'restricted' : 'open'}`}>
                  {restricted ? 'Uzavřeno (vývoj)' : 'Otevřeno'}
                </span>
                <button
                  type="button"
                  className="module-dev-toggle"
                  disabled={!!saving}
                  onClick={() => toggle(key)}
                >
                  {saving === MODULE_DEV_KEYS[key]
                    ? '…'
                    : restricted
                      ? 'Otevřít pro zaměstnance'
                      : 'Uzavřít (jen ředitel / ekonom / IT)'}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
